import { type ComponentProps, createContext, useContext, useId } from 'react';

import { Menu, type MenuRootProps, Portal } from '@ark-ui/react';
import { CaretRight } from '@phosphor-icons/react/dist/csr/CaretRight';
import { Check } from '@phosphor-icons/react/dist/csr/Check';
import { Circle } from '@phosphor-icons/react/dist/csr/Circle';

import type { ActionColor, Scale2 } from '../../lib/types';
import { cn } from '../../lib/utils';

type MenuContentProps = ComponentProps<typeof Menu.Content> & {
	size?: Scale2;
	variant?: 'solid' | 'soft';
	align?: string;
	sideOffset?: number;
};

interface MenuStyleContextValue {
	size: Scale2;
	variant: 'solid' | 'soft';
}

const MenuStyleContext = createContext<MenuStyleContextValue>({
	size: '2',
	variant: 'solid'
});

function MenuContent({
	className,
	size = '2',
	variant = 'solid',
	align: _align,
	sideOffset: _sideOffset,
	...props
}: MenuContentProps) {
	return (
		<MenuStyleContext.Provider value={{ size, variant }}>
			<Portal>
				<Menu.Positioner className="pt-menu-positioner">
					<Menu.Content
						className={cn('pt-menu-content', className)}
						data-size={size}
						data-variant={variant}
						{...props}
					/>
				</Menu.Positioner>
			</Portal>
		</MenuStyleContext.Provider>
	);
}

type MenuItemProps = Omit<ComponentProps<typeof Menu.Item>, 'value'> & {
	value?: string;
	color?: ActionColor;
	shortcut?: string;
};

function MenuItem({ className, color, shortcut, children, value, ...props }: MenuItemProps) {
	const fallbackValue = useId();

	return (
		<Menu.Item
			className={cn('pt-menu-item group', className)}
			data-color={color}
			value={value ?? fallbackValue}
			{...props}
		>
			{children}
			{shortcut && <span className="pt-menu-shortcut">{shortcut}</span>}
		</Menu.Item>
	);
}

function MenuCheckboxItem({
	className,
	value,
	children,
	...props
}: ComponentProps<typeof Menu.CheckboxItem>) {
	const fallbackValue = useId();

	return (
		<Menu.CheckboxItem
			className={cn('pt-menu-item', className)}
			value={value ?? fallbackValue}
			{...props}
		>
			<Menu.ItemIndicator className="pt-menu-item-indicator">
				<Check size={14} weight="bold" />
			</Menu.ItemIndicator>
			<Menu.ItemText>{children}</Menu.ItemText>
		</Menu.CheckboxItem>
	);
}

function MenuRadioGroup({ className, ...props }: ComponentProps<typeof Menu.RadioItemGroup>) {
	return <Menu.RadioItemGroup className={className} {...props} />;
}

function MenuRadioItem({
	className,
	value,
	children,
	...props
}: ComponentProps<typeof Menu.RadioItem>) {
	const fallbackValue = useId();

	return (
		<Menu.RadioItem
			className={cn('pt-menu-item', className)}
			value={value ?? fallbackValue}
			{...props}
		>
			<Menu.ItemIndicator className="pt-menu-item-indicator">
				<Circle size={6} weight="fill" />
			</Menu.ItemIndicator>
			<Menu.ItemText>{children}</Menu.ItemText>
		</Menu.RadioItem>
	);
}

function MenuGroup({ className, ...props }: ComponentProps<typeof Menu.ItemGroup>) {
	return <Menu.ItemGroup className={className} {...props} />;
}

function MenuLabel({ className, ...props }: ComponentProps<typeof Menu.ItemGroupLabel>) {
	return <Menu.ItemGroupLabel className={cn('pt-menu-label', className)} {...props} />;
}

function MenuSeparator({ className, ...props }: ComponentProps<typeof Menu.Separator>) {
	return <Menu.Separator className={cn('pt-menu-separator', className)} {...props} />;
}

function MenuSubRoot({
	positioning = { placement: 'right-start', gutter: 4 },
	...props
}: MenuRootProps) {
	return <Menu.Root positioning={positioning} {...props} />;
}

function MenuSubTrigger({
	className,
	children,
	...props
}: ComponentProps<typeof Menu.TriggerItem>) {
	return (
		<Menu.TriggerItem className={cn('pt-menu-trigger-item pt-menu-item', className)} {...props}>
			<span className="pt-menu-trigger-label">{children}</span>
			<span className="pt-menu-shortcut">
				<CaretRight size={14} weight="bold" />
			</span>
		</Menu.TriggerItem>
	);
}

function MenuSubContent({
	className,
	size,
	variant,
	align: _align,
	sideOffset: _sideOffset,
	...props
}: MenuContentProps) {
	const menuStyle = useContext(MenuStyleContext);
	const resolvedSize = size ?? menuStyle.size;
	const resolvedVariant = variant ?? menuStyle.variant;

	return (
		<Portal>
			<Menu.Positioner className="pt-menu-positioner">
				<Menu.Content
					className={cn('pt-menu-content pt-menu-sub-content', className)}
					data-size={resolvedSize}
					data-variant={resolvedVariant}
					{...props}
				/>
			</Menu.Positioner>
		</Portal>
	);
}

export {
	MenuContent,
	MenuItem,
	MenuCheckboxItem,
	MenuRadioGroup,
	MenuRadioItem,
	MenuGroup,
	MenuLabel,
	MenuSeparator,
	MenuSubRoot,
	MenuSubTrigger,
	MenuSubContent,
	type MenuContentProps,
	type MenuItemProps
};
